import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Users, HeartHandshake, ShieldCheck } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PILLARS = [
  { icon: Leaf, title: "Sustainable sourcing", body: "Preference for partners with ISO 14001 environmental management, verified emissions reporting, and closed-loop material recycling. Sustainability is a supplier-qualification input, not an afterthought." },
  { icon: Users, title: "Fair labour, safe factories", body: "Every audited partner adheres to ILO core conventions, transparent wage practices, and a documented HSE program. Third-party social audits (Sedex / SMETA) preferred for repeat work." },
  { icon: HeartHandshake, title: "Community programs", body: "A share of NEXORIQ's annual margin is directed to STEM scholarships in the manufacturing regions we serve, and to skills-training centres in tier-2 industrial towns." },
  { icon: ShieldCheck, title: "Ethical governance", body: "Zero-tolerance anti-bribery policy aligned to the UK Bribery Act and FCPA. Modern-slavery clauses in every partner contract. Whistle-blower channel via source@nexoriq.co." },
];

export default function Corporate() {
  const [press, setPress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/press-releases`)
      .then((res) => setPress(res.data || []))
      .catch(() => setPress([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-corporate">
      <Seo
        title="Corporate Responsibility & Social Impact"
        description="How NEXORIQ sources matters as much as what we source. Four commitments — sustainable sourcing, fair labour, community investment, ethical governance — run through every partner relationship."
        path="/corporate"
        structuredData={ORG_LD}
      />

      {/* Hero */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24" data-testid="corp-hero">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Corporate Responsibility · CSR</div>
        <RevealLines
          as="h1"
          lines={[<>Doing the work,</>, <>and <span className="text-nx-copper">doing right by it.</span></>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <p className="mt-8 max-w-2xl text-nx-bg/75 leading-relaxed text-lg">
          Sourcing shifts value across borders. How we source matters as much as what we source —
          which is why four commitments run through every partner relationship.
        </p>
      </section>

      {/* CSR pillars */}
      <section className="px-6 md:px-12 lg:px-20 py-24" data-testid="csr-section">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Our commitments</div>
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy max-w-4xl">
          Four commitments. One accountable partner.
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-nx-navy/20 border border-nx-navy/25">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ delay: i * 0.06, duration: 0.6 }}
                className="bg-nx-bg p-8 md:p-10"
                data-testid={`csr-pillar-${i}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon size={26} strokeWidth={1.6} className="text-nx-copper" />
                  <span className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-nx-copper">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-tight">{p.title}</h3>
                <p className="mt-4 text-nx-navy2 leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Press */}
      <section className="bg-nx-bg2 px-6 md:px-12 lg:px-20 py-24" data-testid="press-section">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Press & newsroom</div>
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy max-w-4xl">
          On record.
        </h2>

        <div className="mt-12 border-t border-nx-navy/25">
          {loading ? (
            <div className="py-12 font-mono text-[0.7rem] uppercase tracking-[0.2em] text-nx-steel2" data-testid="press-loading">→ Loading…</div>
          ) : press.length === 0 ? (
            <div className="py-12 text-nx-navy2" data-testid="press-empty">No press releases yet.</div>
          ) : press.map((r, i) => (
            <motion.article
              key={r.id || i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-12 border-b border-nx-navy/25"
              data-testid={`press-item-${i}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-copper">{r.date}</span>
              </div>
              <div className="col-span-12 md:col-span-8">
                <h3 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy leading-tight">{r.title}</h3>
                <p className="mt-3 text-nx-navy2 leading-relaxed max-w-2xl">{r.excerpt}</p>
              </div>
              <div className="col-span-12 md:col-span-2 md:text-right">
                <a href="mailto:source@nexoriq.co?subject=Press enquiry" className="inline-flex items-center gap-1 border-b border-nx-copper pb-0.5 uppercase tracking-[0.14em] text-[0.65rem] text-nx-copper hover:text-nx-navy transition-colors">
                  Press enquiry <ArrowUpRight size={12} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
        <p className="mt-6 text-xs text-nx-steel2">Press contact: <a href="mailto:source@nexoriq.co" className="nx-underline">source@nexoriq.co</a></p>
      </section>

      {/* CTA — become a partner */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5.4vw] text-nx-bg">
              Manufacturer or supplier?
            </h2>
            <p className="mt-6 text-nx-bg/75 max-w-xl leading-relaxed">
              If your factory holds the codes, capacity and discipline, we'd like to talk. Apply to
              join the NEXORIQ partner network and access briefs from Western buyers already
              qualified to your capability sheet.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/partners" className="nx-btn on-dark" data-testid="corporate-partners-cta">
              Become a partner <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
